var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { createContext } from '../../utils/createContext';
import { Toast } from './Toast';
var _a = createContext(), Context = _a[0], useContext = _a[1];
/**
 * # useToasts
 *
 * 실제 토스트를 사용하는 훅
 * 모든 컴포넌트 필요없이, 토스트 컴포넌트로 호출만 하면 됩니다.
 * 다만 상단에 ThemeProvider가 필수적으로 존재해야 합니다.
 *
 * @example ```tsx
 * toast.message('링크를 복사했습니다.')
 * toast.success({ text: '링크를 복사했습니다.',})
 * toast.error({ text: '링크를 복사했습니다.',})
 * ```
 */
export function useToasts() {
    var state = useContext().state;
    return {
        message: state.message,
        success: state.success,
        error: state.error
    };
}
var toastUniqueCount = 0;
/**
 * # ToastProvider
 *
 * 실제로 사용되지 않습니다.
 * 이 Provider는 ThemeProvider에 적용되어 있습니다.
 * 별도로 사용하지 마시고, ThemeProvider를 사용해서 함께 사용하세요.
 */
export var ToastProvider = function (_a) {
    var children = _a.children;
    var _b = useState({
        toastList: []
    }), state = _b[0], setState = _b[1];
    return (<Context state={{
            toastList: state.toastList,
            message: message,
            success: success,
            error: error,
            remove: remove
        }}>
      {children}
      <ToastRoot />
    </Context>);
    function message(param) {
        call(param, 'primary');
    }
    function success(param) {
        call(param, 'success');
    }
    function error(param) {
        call(param, 'error');
    }
    function call(param, type) {
        if (typeof param === 'string') {
            setState(function (state_) { return (__assign(__assign({}, state_), { toastList: __spreadArray(__spreadArray([], state_.toastList, true), [{ text: param, toastUniqueCount: toastUniqueCount++, type: type }], false) })); });
        }
        else {
            setState(function (state_) { return (__assign(__assign({}, state_), { toastList: __spreadArray(__spreadArray([], state_.toastList, true), [__assign(__assign({}, param), { toastUniqueCount: toastUniqueCount++, type: type })], false) })); });
        }
    }
    function remove(uniqueId) {
        setState(function (state_) {
            return __assign(__assign({}, state_), { toastList: state_.toastList.filter(function (it) { return it.toastUniqueCount !== uniqueId; }) });
        });
    }
};
var ToastRoot = function () {
    var state = useContext().state;
    var ref = useRef(null);
    var _a = useState(false), hover = _a[0], setHover = _a[1];
    useEffect(function () {
        var _a, _b;
        var mouseEnter = function () {
            setHover(true);
        };
        var mouseOut = function () {
            setHover(false);
        };
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.addEventListener('mouseenter', mouseEnter);
        (_b = ref.current) === null || _b === void 0 ? void 0 : _b.addEventListener('mouseleave', mouseOut);
        return function () {
            var _a, _b;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('mouseenter', mouseEnter);
            (_b = ref.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('mouseleave', mouseOut);
        };
    }, []);
    useEffect(function () {
        if (state.toastList.length === 0) {
            setHover(false);
        }
    }, [state.toastList]);
    if (typeof document === 'undefined')
        return <></>;
    return ReactDOM.createPortal(<ToastRootLayer ref={ref}>
      {state.toastList.map(function (it, idx, arr) { return (<Toast hover={hover} {...it} order={arr.length - 1 - idx} key={it.toastUniqueCount} remove={function () { return state.remove(it.toastUniqueCount); }}/>); })}
    </ToastRootLayer>, document.body);
};
var ToastRootLayer = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  position: fixed;\n  right: 1.5rem;\n  bottom: 1.5rem;\n  max-width: 420px;\n  transition: all 0.4s ease;\n\n  ", "\n\n  @media (max-width: 440px) {\n    max-width: 90vw;\n    right: 5vw;\n  }\n\n  & > div:not(:first-of-type)::after {\n    content: '';\n    position: absolute;\n    left: 0;\n    right: 0;\n    top: calc(100% + 1px);\n    width: 100%;\n    height: 20px;\n    background: transparent;\n  }\n\n  & > div:nth-last-of-type(n + 4) {\n    opacity: 0 !important;\n    pointer-events: none;\n  }\n\n  &:hover {\n    transform: translate3d(0, -10px, 0);\n  }\n"], ["\n  position: fixed;\n  right: 1.5rem;\n  bottom: 1.5rem;\n  max-width: 420px;\n  transition: all 0.4s ease;\n\n  ", "\n\n  @media (max-width: 440px) {\n    max-width: 90vw;\n    right: 5vw;\n  }\n\n  & > div:not(:first-of-type)::after {\n    content: '';\n    position: absolute;\n    left: 0;\n    right: 0;\n    top: calc(100% + 1px);\n    width: 100%;\n    height: 20px;\n    background: transparent;\n  }\n\n  & > div:nth-last-of-type(n + 4) {\n    opacity: 0 !important;\n    pointer-events: none;\n  }\n\n  &:hover {\n    transform: translate3d(0, -10px, 0);\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    z-index: ", ";\n  "], ["\n    z-index: ", ";\n  "])), theme.layers.SNACKBAR);
});
var templateObject_1, templateObject_2;
//# sourceMappingURL=ToastRoot.jsx.map